import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { SearchResultsPage } from './pages/SearchResultsPage';
import NotFoundPage from './pages/NotFoundPage';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from './shared/components/ErrorBoundary';
import Layout from './components/layout/Layout';
import { BoardPage } from './pages/BoardPage';
import { RedirectToBoard } from './components/routing/RedirectToBoard';
import LogsPage from './pages/LogsPage';
import DocumentationPage from './pages/DocumentationPage';
import DocumentationModulePage from './pages/DocumentationModulePage';
import BoardApiPage from './pages/BoardApiPage';
import SettingsPage from './pages/SettingsPage';
import Dashboard from './pages/Dashboard';
import OnboardingWizard from './pages/OnboardingWizard';
import ComingSoonPage from './pages/ComingSoonPage';
import ProjectOverviewPage from './pages/ProjectOverviewPage';
import MonitorPage from './pages/MonitorPage';
import TicketListPage from './pages/TicketListPage';
import TicketDetailPage from './pages/TicketDetailPage';
import StatusPage from './pages/StatusPage';
import StatusDashboardPage from './pages/StatusDashboardPage';
import IncidentDetailPage from './pages/IncidentDetailPage';
import MaintenanceSchedulePage from './pages/MaintenanceSchedulePage';
import MaintenanceAdminPage from './pages/MaintenanceAdminPage';
import PublicStatusPage from './pages/PublicStatusPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import TemplatesPage from './pages/TemplatesPage';
import DocumentationHubPage from './pages/DocumentationHubPage';
import DocumentationReader from './pages/DocumentationReader';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { AppearanceProvider } from './components/AppearanceProvider';
import { ToastProvider } from './contexts/ToastContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { MarthaProvider } from './contexts/MarthaContext';
import { UserProvider } from './contexts/UserContext';
import { AuthProvider } from './contexts/AuthContext';
import { CollaborativeProvider } from './contexts/CollaborativeContext';
import LoginPage from './pages/LoginPage';
import LogoutPage from './pages/LogoutPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import { ToastContainer } from './components/ui/Toast';
import { PaneProvider } from './shared/contexts/PaneContext';
import { PaneErrorFallback } from './shared/components/PaneErrorFallback';
import { PaneLoader } from './shared/components/PaneLoader';
import { ProjectRouter, RedirectToProject, IssueRedirect, ProtectedRoute, WorkspaceRouter } from './components/routing';
import InviteAcceptPage from './pages/InviteAcceptPage';
import { NotificationProvider } from './components/notifications/index.js';
import { ToastContainer as NotificationToastContainer } from './components/notifications/ToastNotification.js';
import { MailSendTray } from './components/email/MailSendTray';
const WorkspaceMembersPage = lazy(() => import('./pages/WorkspaceMembersPage'));

// Lazy load pane layout components for code splitting
const SuccessSupportPane = lazy(() => import('./panes/success-support'));
const GtmPane = lazy(() => import('./panes/gtm'));
const ArchiePane = lazy(() => import('./panes/archie'));

// Pane route children — defined here, not in nested <Routes> inside panes.
// This is the CRITICAL architectural pattern: one router, all routes visible,
// no nested <Routes> that cause route-ranking conflicts and pane bounce bugs.
import { GtmRouteChildren } from './panes/gtm/routes';
import { ArchieRouteChildren } from './panes/archie/routes';
import { SuccessSupportRouteChildren } from './panes/success-support/routes';
import { OpsRouteChildren } from './panes/ops/routes';
import { DocumentationRouteChildren } from './panes/documentation/routes';

const OpsPane = lazy(() => import('./panes/ops'));
const DocumentationPane = lazy(() => import('./panes/documentation'));

// Dev-only playground (tree-shaken from production builds)
const PlaygroundShell = lazy(() => import('./playground/PlaygroundShell'));

// Lazy load KB pages
const PublicKBArticlePage = lazy(() => import('./pages/PublicKBArticlePage'));
const AuthKBArticlePage = lazy(() => import('./pages/AuthKBArticlePage'));
import { KBRouteGuard } from './components/KBRouteGuard';

// Lazy load pages for code splitting
const SprintBoardPage = lazy(() => import('./pages/SprintBoardPage'));
const ModelManagementPage = lazy(() => import('./pages/ModelManagementPage'));
const ObservabilityPage = lazy(() => import('./pages/ObservabilityPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const EvidencePage = lazy(() => import('./pages/MonitorPage'));
const BuildConfigPage = lazy(() => import('./pages/BuildConfigPage'));
const EnvironmentPage = lazy(() => import('./pages/EnvironmentPage'));
const QueueManagementPage = lazy(() => import('./pages/QueueManagementPage'));
const TestingPage = lazy(() => import('./pages/TestingPage'));
const StagingPage = lazy(() => import('./panes/archie/pages/StagingPage'));

// Guide analytics admin page (SAS-290)
const GuideAnalyticsPage = lazy(() => import('./pages/GuideAnalyticsPage'));

// Document trash page
const DocumentTrashPage = lazy(() => import('./pages/DocumentTrashPage'));

// Cross-project tasks page
const TasksPage = lazy(() => import('./pages/TasksPage'));

// Mission Control Tower (ATC)
const MissionControlPage = lazy(() => import('./mission-control-tower/MissionControlPage'));

// Topic pop-out page (no layout, full workspace)
const TopicPage = lazy(() => import('./pages/TopicPage'));

// Create React Query client with optimized cache settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      gcTime: 300000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) return false;
        }
        return failureCount < 1;
      },
    },
  },
});

/**
 * DefaultWorkspaceRedirect — redirects / to /w/archie/pr
 * URL is truth. No localStorage reads for routing decisions.
 */
function DefaultWorkspaceRedirect() {
  return <Navigate to="/w/archie/product" replace />;
}

/**
 * DefaultPaneRedirect — redirects /w/:wsSlug to /w/:wsSlug/pr
 * URL is truth. No localStorage reads for routing decisions.
 * Preserves query params (e.g., ?topic=xxx) and hash.
 */
function DefaultPaneRedirect() {
  const { wsSlug } = useParams<{ wsSlug: string }>();
  const location = useLocation();
  return <Navigate to={`/w/${wsSlug}/product${location.search}${location.hash}`} replace />;
}

/**
 * MailboxEnableRedirect — /w/:wsSlug/oauth/nylas/* → the inbox, where a heyarchie.ai
 * mailbox is enabled directly (ADR-0011 retired the Nylas sign-in callback).
 * Built from wsSlug: a relative "../sales/inbox" in a splat route resolves
 * against the splat in React Router 7.
 */
function MailboxEnableRedirect() {
  const { wsSlug } = useParams<{ wsSlug: string }>();
  return <Navigate to={`/w/${wsSlug}/sales/inbox`} replace />;
}

/**
 * Legacy-route redirect that preserves the document id. React Router's
 * <Navigate to="../documents"> drops the param, so /do/abc123 would land on
 * /documents with no selected doc. Reads either `docId` or `documentId` from
 * useParams — some legacy routes used each name.
 */
function RedirectToDocument() {
  const params = useParams<{ docId?: string; documentId?: string }>();
  const id = params.docId || params.documentId;
  return <Navigate to={id ? `../documents/${id}` : '../documents'} replace />;
}

/**
 * Project routes shared between /w/:wsSlug/pr/:shortId and legacy /pr/:shortId
 */
function ProjectRoutes() {
  return (
    <>
      {/* Project Overview */}
      <Route index element={<ProjectOverviewPage />} />
      <Route path="settings" element={<Navigate to="/settings/project" replace />} />
      <Route path="settings/dev" element={<SettingsPage />} />

      {/* Design */}
      <Route path="features" element={<ComingSoonPage title="Features" />} />
      <Route path="roadmap" element={<ComingSoonPage title="Roadmap" />} />
      <Route path="ideas" element={<ComingSoonPage title="Ideas" />} />

      {/* Documentation */}
      <Route path="documents/:docId?" element={<DocumentationReader />} />
      <Route path="trash" element={<Suspense fallback={null}><DocumentTrashPage /></Suspense>} />
      <Route path="docs" element={<Navigate to="../documents" replace />} />
      <Route path="docs/requirements" element={<Navigate to="../documents" replace />} />
      <Route path="docs/requirements/:docId" element={<RedirectToDocument />} />
      <Route path="docs/specifications" element={<Navigate to="../documents" replace />} />
      <Route path="docs/specifications/:docId" element={<RedirectToDocument />} />

      {/* Boards */}
      <Route path="boards" element={<BoardPage />} />
      <Route path="boards/:boardId" element={<BoardPage />} />
      <Route path="boards/:boardId/is/:issueId" element={<BoardPage />} />
      <Route path="boards/:boardId/:view" element={<BoardPage />} />

      {/* Epics */}
      <Route path="epics" element={<BoardPage epicFilter />} />
      <Route path="epics/:boardId" element={<BoardPage epicFilter />} />
      <Route path="epics/:boardId/is/:issueId" element={<BoardPage epicFilter />} />
      <Route path="epics/:boardId/:view" element={<BoardPage epicFilter />} />

      {/* Stories */}
      <Route path="stories" element={<BoardPage storyFilter />} />
      <Route path="stories/:boardId" element={<BoardPage storyFilter />} />
      {/* BoardPage builds stories/<board>/is/<card>; without this it matched :view="is". */}
      <Route path="stories/:boardId/is/:issueId" element={<BoardPage storyFilter />} />
      <Route path="stories/:boardId/:view" element={<BoardPage storyFilter />} />
      <Route path="stories/:boardId/:view/:issueId" element={<BoardPage storyFilter />} />

      {/* Development */}
      <Route path="monitor" element={<Suspense fallback={null}><EvidencePage /></Suspense>} />
      <Route path="sprints/:sprintId?" element={<Suspense fallback={null}><SprintBoardPage /></Suspense>} />
      <Route path="worktrees" element={<ComingSoonPage title="Worktrees" />} />
      <Route path="testing" element={<Suspense fallback={null}><TestingPage /></Suspense>} />
      <Route path="staging" element={<Suspense fallback={null}><StagingPage /></Suspense>} />
      <Route path="coder" element={<MonitorPage initialTab="sprites" />} />

      {/* Metrics */}
      <Route path="automations" element={<ComingSoonPage title="Automations & Gates" />} />

      {/* Operations */}
      <Route path="tower" element={<Suspense fallback={null}><MissionControlPage /></Suspense>} />
      <Route path="mc" element={<Suspense fallback={null}><MissionControlPage /></Suspense>} />
      <Route path="mission-control" element={<Suspense fallback={null}><MissionControlPage /></Suspense>} />
      <Route path="security" element={<Suspense fallback={null}><SecurityPage /></Suspense>} />
      <Route path="build-config" element={<Suspense fallback={null}><BuildConfigPage /></Suspense>} />
      <Route path="environment" element={<Suspense fallback={null}><EnvironmentPage /></Suspense>} />
      <Route path="queue" element={<Suspense fallback={null}><QueueManagementPage /></Suspense>} />

      {/* AI Tools */}
      <Route path="editor" element={<Navigate to="../documents" replace />} />
      <Route path="editor/:documentId" element={<RedirectToDocument />} />
      <Route path="templates" element={<TemplatesPage />} />

      {/* Legacy 2-letter code redirects */}
      <Route path="do/:docId" element={<RedirectToDocument />} />
      <Route path="do" element={<Navigate to="../documents" replace />} />
      <Route path="tr" element={<Navigate to="../trash" replace />} />
      <Route path="bo" element={<Navigate to="../boards" replace />} />
      {/* Keep the board and the open card: <Navigate to="../boards"> dropped them (RedirectToBoard.tsx). */}
      <Route path="bo/:boardId" element={<RedirectToBoard base="boards" />} />
      <Route path="bo/:boardId/is/:issueId" element={<RedirectToBoard base="boards" />} />
      <Route path="bo/:boardId/:view" element={<RedirectToBoard base="boards" />} />
      <Route path="ep" element={<Navigate to="../epics" replace />} />
      <Route path="ep/:boardId" element={<RedirectToBoard base="epics" />} />
      <Route path="ep/:boardId/is/:issueId" element={<RedirectToBoard base="epics" />} />
      <Route path="ep/:boardId/:view" element={<RedirectToBoard base="epics" />} />
      <Route path="ev" element={<Navigate to="../monitor" replace />} />
      <Route path="sp/:sprintId?" element={<Navigate to="../sprints" replace />} />
      <Route path="sp" element={<Navigate to="../sprints" replace />} />
      <Route path="tt" element={<Navigate to="../testing" replace />} />
      <Route path="sg" element={<Navigate to="../staging" replace />} />
      <Route path="cr" element={<Navigate to="../coder" replace />} />
      <Route path="am" element={<Navigate to="../automations" replace />} />
      <Route path="sy" element={<Navigate to="../security" replace />} />
      <Route path="bf" element={<Navigate to="../build-config" replace />} />
      <Route path="bc" element={<Navigate to="../environment" replace />} />
      <Route path="qu" element={<Navigate to="../queue" replace />} />
      <Route path="me" element={<Navigate to="../documents" replace />} />
      <Route path="me/:documentId" element={<RedirectToDocument />} />
      <Route path="tp" element={<Navigate to="../templates" replace />} />
      <Route path="wt" element={<Navigate to="../worktrees" replace />} />
      <Route path="wf" element={<Navigate to="../monitor" replace />} />
      <Route path="cd" element={<Navigate to="../monitor" replace />} />
      <Route path="tl" element={<Navigate to="../monitor" replace />} />
      <Route path="av" element={<Navigate to="../monitor" replace />} />
      <Route path="sprite-env" element={<Navigate to="../environment" replace />} />
      <Route path="observability" element={<Navigate to="../monitor" replace />} />

      {/* Legacy long-name redirects */}
      <Route path="board" element={<Navigate to="../boards" replace />} />
      <Route path="board/*" element={<Navigate to="../boards" replace />} />
      <Route path="evidence" element={<Navigate to="../monitor" replace />} />
      <Route path="workflow" element={<Navigate to="../monitor" replace />} />
      <Route path="completed" element={<Navigate to="../monitor" replace />} />
      <Route path="magic-editor" element={<Navigate to="../documents" replace />} />
      <Route path="magic-editor/:documentId" element={<RedirectToDocument />} />
      <Route path="telemetry" element={<Navigate to="../monitor" replace />} />
      <Route path="activity" element={<Navigate to="../monitor" replace />} />
      <Route path="tracker" element={<Navigate to="../boards" replace />} />
      <Route path="tracker/*" element={<Navigate to="../boards" replace />} />
    </>
  );
}


function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <PreferencesProvider>
        <AppearanceProvider>
        <ToastProvider>
          <ErrorBoundary>
          <Router>
            <AuthProvider>
            <UserProvider>
              <Routes>
                {/* ============================================ */}
                {/* PUBLIC ROUTES (outside ProtectedRoute) */}
                {/* ============================================ */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/logout" element={<LogoutPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/invite/:token" element={<InviteAcceptPage />} />
                <Route path="/status" element={<PublicStatusPage />} />

                {/* Dev-only playground — tree-shaken from prod builds */}
                {import.meta.env.DEV && (
                  <Route path="/playground/*" element={
                    <Suspense fallback={<div style={{ padding: 24 }}>Loading playground…</div>}>
                      <PlaygroundShell />
                    </Suspense>
                  } />
                )}

                {/* ============================================ */}
                {/* PROTECTED ROUTES (require authentication) */}
                {/* ============================================ */}
                <Route path="/*" element={
                  <ProtectedRoute>
              <NotificationProvider>
              <MarthaProvider>
                {/* Provider chain: Workspace → Pane → Project → Collaborative */}
                <WorkspaceProvider>
                <PaneProvider>
                  <ProjectProvider>
                    <CollaborativeProvider>
                      <Layout>
                  <Routes>

                  {/* ============================================ */}
                  {/* WORKSPACE-SCOPED ROUTES: /w/:wsSlug/... */}
                  {/* ============================================ */}
                  <Route path="/w/:wsSlug" element={<WorkspaceRouter />}>

                    {/* Workspace root → redirect to last-used pane */}
                    <Route index element={<DefaultPaneRedirect />} />

                    {/* ── Product pane (project-scoped) ── */}
                    <Route path="product/:shortId" element={<ProjectRouter />}>
                      {ProjectRoutes()}
                    </Route>

                    {/* ─────────────────────────────────────────────── */}
                    {/* PANE LAYOUT ROUTES                              */}
                    {/*                                                 */}
                    {/* Architecture: each pane is a LAYOUT route that  */}
                    {/* renders providers + <Outlet />. Route children   */}
                    {/* are defined HERE in the parent router — NOT in  */}
                    {/* nested <Routes> inside pane modules.            */}
                    {/*                                                 */}
                    {/* This ensures ONE router has full visibility of  */}
                    {/* all routes, preventing route-ranking conflicts  */}
                    {/* that caused the pane bounce bug.                */}
                    {/* ─────────────────────────────────────────────── */}

                    {/* ── Sales & Marketing pane (workspace-scoped) ── */}
                    <Route path="sales" element={
                      <ErrorBoundary fallback={<PaneErrorFallback paneName="Sales & Marketing" />} resetKey="sales-pane">
                        <Suspense fallback={<PaneLoader message="Loading Sales & Marketing..." />}>
                          <GtmPane />
                        </Suspense>
                      </ErrorBoundary>
                    }>
                      {GtmRouteChildren()}
                    </Route>

                    {/* ── Operations pane (workspace-scoped) ── */}
                    <Route path="operations" element={
                      <ErrorBoundary fallback={<PaneErrorFallback paneName="Operations" />} resetKey="ops-pane">
                        <Suspense fallback={<PaneLoader message="Loading Operations..." />}>
                          <OpsPane />
                        </Suspense>
                      </ErrorBoundary>
                    }>
                      {OpsRouteChildren()}
                    </Route>

                    {/* ── Archie pane (workspace-scoped) ── */}
                    <Route path="archie" element={
                      <ErrorBoundary fallback={<PaneErrorFallback paneName="Archie" />} resetKey="archie-pane">
                        <Suspense fallback={<PaneLoader message="Loading Archie..." />}>
                          <ArchiePane />
                        </Suspense>
                      </ErrorBoundary>
                    }>
                      {ArchieRouteChildren()}
                    </Route>

                    {/* ── Success & Support pane (workspace-scoped) ── */}
                    <Route path="success" element={
                      <ErrorBoundary fallback={<PaneErrorFallback paneName="Success & Support" />} resetKey="success-pane">
                        <Suspense fallback={<PaneLoader message="Loading Success & Support..." />}>
                          <SuccessSupportPane />
                        </Suspense>
                      </ErrorBoundary>
                    }>
                      {SuccessSupportRouteChildren()}
                    </Route>

                    {/* ── Documentation pane (org-scoped) ── */}
                    <Route path="docs" element={
                      <ErrorBoundary fallback={<PaneErrorFallback paneName="Documentation" />} resetKey="documentation-pane">
                        <Suspense fallback={<PaneLoader message="Loading Documentation..." />}>
                          <DocumentationPane />
                        </Suspense>
                      </ErrorBoundary>
                    }>
                      {DocumentationRouteChildren()}
                    </Route>

                    {/* ── Bare-prefix redirects (no project selected) ── */}
                    <Route path="product" element={<RedirectToProject path="" prefix="product" />} />

                    {/* ── Legacy pane prefix redirects (2-letter → semantic) ── */}
                    <Route path="pr/:shortId/*" element={<LegacyPrefixRedirectInWorkspace />} />
                    <Route path="pr" element={<RedirectToProject path="" prefix="product" />} />
                    <Route path="sm/*" element={<LegacyPrefixRedirectInWorkspace />} />
                    <Route path="sm" element={<Navigate to="sales" replace />} />
                    <Route path="ar/*" element={<LegacyPrefixRedirectInWorkspace />} />
                    <Route path="ar" element={<Navigate to="archie" replace />} />
                    <Route path="su/*" element={<LegacyPrefixRedirectInWorkspace />} />
                    <Route path="su" element={<Navigate to="success" replace />} />
                    <Route path="op/*" element={<LegacyPrefixRedirectInWorkspace />} />
                    <Route path="op" element={<Navigate to="operations" replace />} />
                    <Route path="dc/*" element={<LegacyPrefixRedirectInWorkspace />} />
                    <Route path="dc" element={<Navigate to="docs" replace />} />

                    {/* Legacy success/* → success/* within workspace */}
                    <Route path="success/*" element={<LegacySuccessRedirect />} />

                    {/* Model Management dashboard (OV3-093) */}
                    <Route path="mm" element={
                      <Suspense fallback={<PaneLoader message="Loading Model Management..." />}>
                        <ModelManagementPage />
                      </Suspense>
                    } />

                    {/* Cross-project task view */}
                    <Route path="tasks" element={
                      <Suspense fallback={<PaneLoader message="Loading Tasks..." />}>
                        <TasksPage />
                      </Suspense>
                    } />
                    {/* Legacy ta → tasks */}
                    <Route path="ta" element={<Navigate to="tasks" replace />} />

                    {/* Workspace-level pages */}
                    <Route path="projects" element={<ProjectsPage />} />
                    <Route path="search" element={<SearchResultsPage />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="onboarding" element={<OnboardingWizard />} />

                    {/* Settings */}
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="settings/dt" element={<SettingsPage />} />
                    <Route path="settings/github" element={<SettingsPage />} />
                    <Route path="settings/github/callback" element={<SettingsPage />} />
                    <Route path="settings/appearance" element={<SettingsPage />} />
                    <Route path="settings/agents" element={<SettingsPage />} />
                    <Route path="settings/prompts" element={<SettingsPage />} />
                    <Route path="settings/plugins" element={<SettingsPage />} />
                    <Route path="settings/integrations" element={<SettingsPage />} />
                    <Route path="ig" element={<SettingsPage />} />
                    <Route path="settings/mcp" element={<Navigate to="../settings/tokens" replace />} />
                    <Route path="settings/tokens" element={<SettingsPage />} />
                    <Route path="settings/skills" element={<SettingsPage />} />
                    <Route path="settings/deployments" element={<SettingsPage />} />
                    <Route path="settings/fleet" element={<SettingsPage />} />
                    <Route path="settings/project" element={<SettingsPage />} />
                    <Route path="settings/project/repo" element={<SettingsPage />} />
                    <Route path="settings/project/agents" element={<SettingsPage />} />
                    <Route path="settings/project/tools" element={<SettingsPage />} />
                    <Route path="settings/dev" element={<SettingsPage />} />
                    <Route path="settings/members" element={<Suspense fallback={null}><WorkspaceMembersPage /></Suspense>} />

                    {/* Legacy settings routes (se → settings) */}
                    <Route path="se" element={<Navigate to="settings" replace />} />
                    <Route path="se/*" element={<LegacySettingsRedirect />} />

                    {/* Help / Support tickets */}
                    <Route path="help/tickets" element={<TicketListPage />} />
                    <Route path="help/tickets/:id" element={<TicketDetailPage />} />

                    {/* Topic workspace — /topics/:id redirects to ?topic=:id */}
                    <Route path="topics/:topicId" element={<LegacyTopicRedirect />} />
                    {/* Legacy to/:id → topics/:id */}
                    <Route path="to/:topicId" element={<LegacyTopicRedirect />} />

                    {/* Global issue redirect */}
                    <Route path="is/:issueId" element={<IssueRedirect />} />

                    {/* Mailboxes are enabled from the inbox, without a sign-in round trip
                        (ADR-0011, MA-481). A stale provider return lands there too. */}
                    <Route path="oauth/nylas/*" element={<MailboxEnableRedirect />} />

                    {/* KB routes */}
                    <Route path="kb/:categorySlug/:articleSlug" element={
                      <Suspense fallback={<PaneLoader message="Loading article..." />}>
                        <PublicKBArticlePage />
                      </Suspense>
                    } />
                    <Route path="help/kb/:categorySlug/:articleSlug" element={
                      <KBRouteGuard>
                        <Suspense fallback={<PaneLoader message="Loading article..." />}>
                          <AuthKBArticlePage />
                        </Suspense>
                      </KBRouteGuard>
                    } />

                    {/* Logs & Docs */}
                    <Route path="logs" element={<LogsPage />} />
                    <Route path="docs" element={<DocumentationPage />} />
                    <Route path="docs/:docId" element={<DocumentationPage />} />
                    <Route path="docs/board-api-explorer" element={<BoardApiPage />} />

                    {/* Status pages — redirect to public /status */}
                    <Route path="status" element={<Navigate to="/status" replace />} />
                    <Route path="status/incidents/:id" element={<Navigate to="/status" replace />} />
                    <Route path="status/maintenance" element={<Navigate to="/status" replace />} />
                    <Route path="status/maintenance/:id" element={<Navigate to="/status" replace />} />
                    <Route path="admin/status/maintenance" element={<MaintenanceAdminPage />} />

                    {/* Guide analytics admin */}
                    <Route path="admin/guides/:id/analytics" element={
                      <Suspense fallback={<div className="p-6 text-gray-400">Loading analytics...</div>}>
                        <GuideAnalyticsPage />
                      </Suspense>
                    } />

                  </Route>

                  {/* ============================================ */}
                  {/* TOPIC POP-OUT (no sidebar/topic bar) */}
                  {/* ============================================ */}
                  <Route path="/topic/:topicId" element={
                    <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-2 border-coral-400 border-t-transparent rounded-full animate-spin" /></div>}>
                      <TopicPage />
                    </Suspense>
                  } />

                  {/* ============================================ */}
                  {/* ROOT REDIRECT → default workspace */}
                  {/* ============================================ */}
                  <Route path="/" element={<DefaultWorkspaceRedirect />} />

                  {/* ============================================ */}
                  {/* LEGACY ROUTES — redirect into /w/:wsSlug */}
                  {/* These catch old bookmarks and redirect once */}
                  {/* ============================================ */}

                  {/* Legacy project routes */}
                  <Route path="/pr/:shortId/*" element={<LegacyPaneRedirect prefix="pr" newPrefix="product" />} />
                  <Route path="/p/:shortId/*" element={<LegacyPaneRedirect prefix="p" newPrefix="product" />} />
                  <Route path="/product/:shortId/*" element={<LegacyPaneRedirect prefix="product" />} />

                  {/* Legacy pane routes */}
                  <Route path="/ar/*" element={<LegacyPaneRedirect prefix="ar" newPrefix="archie" />} />
                  <Route path="/sm/*" element={<LegacyPaneRedirect prefix="sm" newPrefix="sales" />} />
                  <Route path="/sm" element={<LegacyPaneRedirect prefix="sm" newPrefix="sales" />} />
                  <Route path="/op/*" element={<LegacyPaneRedirect prefix="op" newPrefix="operations" />} />
                  <Route path="/success/*" element={<LegacyPaneRedirect prefix="success" />} />
                  <Route path="/su/*" element={<LegacyPaneRedirect prefix="su" newPrefix="success" />} />
                  <Route path="/dc/*" element={<LegacyPaneRedirect prefix="dc" newPrefix="docs" />} />

                  {/* Legacy flat routes */}
                  <Route path="/se/*" element={<LegacyPaneRedirect prefix="se" newPrefix="settings" />} />
                  <Route path="/settings" element={<Navigate to="/w/archie/settings" replace />} />
                  <Route path="/projects" element={<LegacyPaneRedirect prefix="projects" />} />
                  <Route path="/projects/:projectId" element={<LegacyPaneRedirect prefix="projects" />} />
                  <Route path="/search" element={<LegacyPaneRedirect prefix="search" />} />
                  <Route path="/dashboard" element={<LegacyPaneRedirect prefix="dashboard" />} />
                  <Route path="/onboarding" element={<LegacyPaneRedirect prefix="onboarding" />} />
                  <Route path="/help/*" element={<LegacyPaneRedirect prefix="help" />} />
                  <Route path="/is/:issueId" element={<LegacyPaneRedirect prefix="is" />} />
                  <Route path="/status/*" element={<Navigate to="/status" replace />} />
                  <Route path="/logs" element={<LegacyPaneRedirect prefix="logs" />} />
                  <Route path="/docs/*" element={<LegacyPaneRedirect prefix="docs" />} />
                  <Route path="/kb/*" element={<LegacyPaneRedirect prefix="kb" />} />
                  <Route path="/oauth/*" element={<LegacyPaneRedirect prefix="oauth" />} />
                  <Route path="/admin/*" element={<LegacyPaneRedirect prefix="admin" />} />

                  {/* Legacy GTM redirects */}
                  <Route path="/gt" element={<Navigate to="/w/archie/sales" replace />} />
                  <Route path="/gt/*" element={<Navigate to="/w/archie/sales" replace />} />
                  <Route path="/gtm" element={<Navigate to="/w/archie/sales" replace />} />
                  <Route path="/gtm/*" element={<Navigate to="/w/archie/sales" replace />} />

                  {/* Catch-all 404 — renders static page, no redirects, no loops */}
                  <Route path="*" element={<NotFoundPage />} />

                      </Routes>
                    </Layout>
                    <ToastContainer />
                    <NotificationToastContainer />
                    {/* Approved sends waiting out their undo window (MA-484) */}
                    <MailSendTray />
                    </CollaborativeProvider>
                  </ProjectProvider>
                </PaneProvider>
                </WorkspaceProvider>
              </MarthaProvider>
              </NotificationProvider>
                  </ProtectedRoute>
                } />
              </Routes>
            </UserProvider>
            </AuthProvider>
          </Router>
          </ErrorBoundary>
        </ToastProvider>
        </AppearanceProvider>
      </PreferencesProvider>
    </QueryClientProvider>
  );
}

/**
 * LegacyTopicRedirect — redirects /w/:wsSlug/to/:topicId to /w/:wsSlug?topic=:topicId
 * Preserves backward compat for old topic URLs.
 */
function LegacyTopicRedirect() {
  const location = useLocation();
  const match = location.pathname.match(/\/w\/([^/]+)\/to\/([a-z0-9]{6,7})/);
  if (!match) return <Navigate to="/" replace />;
  const [, wsSlug, topicId] = match;
  const params = new URLSearchParams(location.search);
  params.set('topic', topicId);
  return <Navigate to={`/w/${wsSlug}?${params.toString()}`} replace />;
}

/**
 * LegacySuccessRedirect — redirects /w/:wsSlug/success/* to /w/:wsSlug/success/*
 * (success is already the canonical prefix, so this just ensures consistency)
 */
function LegacySuccessRedirect() {
  const location = useLocation();
  const newPath = location.pathname.replace(/\/success(\/|$)/, '/success$1') + location.search + location.hash;
  return <Navigate to={newPath} replace />;
}

/**
 * LegacyPaneRedirect — redirects old flat URLs into /w/archie/:rest
 * URL is truth. No localStorage reads for routing decisions.
 * If newPrefix is provided, maps the old prefix to the new one (e.g., pr → product).
 */
function LegacyPaneRedirect({ prefix, newPrefix }: { prefix: string; newPrefix?: string }) {
  const location = useLocation();
  const targetPrefix = newPrefix ?? prefix;
  // Strip the leading /prefix from pathname and rebuild under /w/archie/targetPrefix/rest
  const rest = location.pathname.replace(new RegExp(`^/${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/?`), '');
  const newPath = `/w/archie/${targetPrefix}${rest ? `/${rest}` : ''}${location.search}${location.hash}`;
  return <Navigate to={newPath} replace />;
}

/**
 * LegacyPrefixRedirectInWorkspace — redirects old 2-letter pane prefixes to semantic names
 * within the workspace scope. E.g., /w/archie/sm/contacts → /w/archie/sales/contacts
 */
function LegacyPrefixRedirectInWorkspace() {
  const location = useLocation();
  const LEGACY_MAP: Record<string, string> = {
    pr: 'product', ar: 'archie', sm: 'sales', su: 'success', op: 'operations', dc: 'docs',
  };
  const wsMatch = location.pathname.match(/^(\/w\/[^/]+)\/([^/]+)(\/.*)?$/);
  if (!wsMatch) return <Navigate to="/" replace />;
  const [, wsBase, oldPrefix, rest = ''] = wsMatch;
  const newPrefix = LEGACY_MAP[oldPrefix] ?? oldPrefix;
  return <Navigate to={`${wsBase}/${newPrefix}${rest}${location.search}${location.hash}`} replace />;
}

/**
 * LegacySettingsRedirect — redirects /w/:wsSlug/se/* to /w/:wsSlug/settings/*
 */
function LegacySettingsRedirect() {
  const location = useLocation();
  const newPath = location.pathname.replace(/\/se(\/|$)/, '/settings$1') + location.search + location.hash;
  return <Navigate to={newPath} replace />;
}

export default App;
